import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import { 
  CheckCircle as ApproveIcon, 
  Cancel as RejectIcon, 
  Warning as SensitiveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import {
  listPendingImages,
  listPendingVideos,
  moderateImage,
  moderateVideo,
  updateImage,
  updateVideo,
} from '../../api/images';
import { useAuthedObjectUrl } from '../../hooks/useAuthedObjectUrl';
import client from '../../api/client';
import { useMobileLayout } from '../../utils/useMobileLayout';


// Media sits behind the JWT authorizer, so a plain <img>/<video> src carries
// no token and 401s. These fetch through the client and render a blob URL.
// Separate components because the hook can't be called inside a .map().
const AuthedThumb = ({ filePath, alt, onClick }) => {
  const { url, failed } = useAuthedObjectUrl('image', filePath);
  if (failed) {
    return (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary', fontSize: 14 }}>
        Preview unavailable
      </Box>
    );
  }
  if (!url) return <Skeleton variant="rectangular" height={200} />;
  return (
    <CardMedia component="img" height="200" image={url} alt={alt} onClick={onClick} sx={{ cursor: 'pointer' }} />
  );
};

const AuthedMedia = ({ kind, filePath, alt }) => {
  const { url, failed } = useAuthedObjectUrl(kind, filePath);
  if (failed) return <Typography color="text.secondary">Preview unavailable</Typography>;
  if (!url) return <Skeleton variant="rectangular" height={320} />;
  return kind === 'video'
    ? <video src={url} controls style={{ maxWidth: '100%', maxHeight: '60vh' }} />
    : <img src={url} alt={alt} style={{ maxWidth: '100%', maxHeight: '60vh' }} />;
};
const ContentModeration = () => {
  const { isMobile } = useMobileLayout();
  const [isLoading, setIsLoading] = useState(true);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContent, setTotalContent] = useState(0);
  const [pageSize] = useState(10);
  const [contentType, setContentType] = useState('images'); // 'images' or 'videos'

  // Fetch pending content with pagination
  const fetchPendingContent = async (pageNum = page, type = contentType) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (type === 'images') {
        const data = await listPendingImages({ page: pageNum, page_size: pageSize });
        setPendingImages(data.images);
        setTotalPages(data.total_pages);
        setTotalContent(data.total);
      } else {
        const data = await listPendingVideos({ page: pageNum, page_size: pageSize });
        setPendingVideos(data.videos);
        setTotalPages(data.total_pages);
        setTotalContent(data.total);
      }
      setPage(pageNum);
    } catch (error) {
      console.error(`Error fetching pending ${type}:`, error);
      setError(`Failed to load pending ${type}. Please try again later.`);
      if (type === 'images') {
        setPendingImages([]);
      } else {
        setPendingVideos([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingContent(1, contentType);
  }, [contentType]);

  const handlePageChange = (event, newPage) => {
    fetchPendingContent(newPage, contentType);
  };

  const handleContentTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setContentType(newValue);
      setPage(1);
    }
  };

  const handleApprove = async (id, type) => {
    setIsSubmitting(true);

    try {
      if (type === 'image') {
        await moderateImage(id, {
          is_approved: true,
          is_sensitive: pendingImages.find(img => img.id === id)?.is_sensitive || false
        });
        // Remove the approved image from the list
        setPendingImages(pendingImages.filter(image => image.id !== id));
      } else {
        await moderateVideo(id, {
          is_approved: true,
          is_sensitive: pendingVideos.find(vid => vid.id === id)?.is_sensitive || false
        });
        // Remove the approved video from the list
        setPendingVideos(pendingVideos.filter(video => video.id !== id));
      }

      // Close dialog if open
      if (openDialog) {
        setOpenDialog(false);
        setSelectedContent(null);
      }
    } catch (error) {
      console.error(`Error approving ${type}:`, error);
      setError(`Failed to approve ${type}. Please try again later.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (id, type) => {
    setIsSubmitting(true);

    try {
      if (type === 'image') {
        await moderateImage(id, {
          is_approved: false,
          is_sensitive: pendingImages.find(img => img.id === id)?.is_sensitive || false
        });
        // Remove the rejected image from the list
        setPendingImages(pendingImages.filter(image => image.id !== id));
      } else {
        await moderateVideo(id, {
          is_approved: false,
          is_sensitive: pendingVideos.find(vid => vid.id === id)?.is_sensitive || false
        });
        // Remove the rejected video from the list
        setPendingVideos(pendingVideos.filter(video => video.id !== id));
      }

      // Close dialog if open
      if (openDialog) {
        setOpenDialog(false);
        setSelectedContent(null);
      }
    } catch (error) {
      console.error(`Error rejecting ${type}:`, error);
      setError(`Failed to reject ${type}. Please try again later.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveAll = async () => {
    if (totalContent === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Use the appropriate endpoint to approve all pending content at once
      const endpoint = contentType === 'images' ? '/images/approve-all' : '/videos/approve-all';
      const response = await client.post(endpoint);

      setSuccess(`Successfully approved ${response.data.count} ${contentType}.`);

      // Refresh the list to show there are no more pending content
      fetchPendingContent(1, contentType);
    } catch (error) {
      console.error(`Error approving all ${contentType}:`, error);
      setError(`Failed to approve all ${contentType}. Please try again later.`);

      // Refresh the list to get the current state
      fetchPendingContent(1, contentType);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleSensitive = async (id, isSensitive, type) => {
    try {
      if (type === 'image') {
        // Update the image in the UI first for immediate feedback
        setPendingImages(pendingImages.map(image => 
          image.id === id ? { ...image, is_sensitive: !isSensitive } : image
        ));

        // Then update in the backend
        await updateImage(id, {
          is_sensitive: !isSensitive
        });
      } else {
        // Update the video in the UI first for immediate feedback
        setPendingVideos(pendingVideos.map(video =>
          video.id === id ? { ...video, is_sensitive: !isSensitive } : video
        ));

        // Then update in the backend
        await updateVideo(id, {
          is_sensitive: !isSensitive
        });
      }
    } catch (error) {
      console.error(`Error updating ${type} sensitivity:`, error);

      // Revert the UI change if the API call fails
      if (type === 'image') {
        setPendingImages(pendingImages.map(image => 
          image.id === id ? { ...image, is_sensitive: isSensitive } : image
        ));
      } else {
        setPendingVideos(pendingVideos.map(video => 
          video.id === id ? { ...video, is_sensitive: isSensitive } : video
        ));
      }

      setError(`Failed to update ${type} sensitivity. Please try again later.`);
    }
  };

  const handleOpenDialog = (content, type) => {
    setSelectedContent({ ...content, contentType: type });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContent(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderContent = () => {
    const contentItems = contentType === 'images' ? pendingImages : pendingVideos;
    
    if (contentItems.length === 0) {
      return <Alert severity="info">No {contentType} pending moderation.</Alert>;
    }

    return (
      <Grid container spacing={3}>
        {contentItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card>
              {contentType === 'images' ? (
                <AuthedThumb
                  filePath={item.file_path}
                  alt={`Image #${item.id}`}
                  onClick={() => handleOpenDialog(item, 'image')}
                />
              ) : (
                <Box 
                  sx={{ 
                    position: 'relative', 
                    height: 200, 
                    backgroundColor: '#000',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOpenDialog(item, 'video')}
                >
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <VideoIcon sx={{ fontSize: 60, color: 'white', opacity: 0.7 }} />
                    <Typography variant="body2" color="white" sx={{ mt: 1 }}>
                      Click to preview video
                    </Typography>
                  </Box>
                </Box>
              )}
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Wildfire #{item.wildfire_id}
                </Typography>
                {item.description && (
                  <Typography variant="body2" gutterBottom>
                    {item.description}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Uploaded by: {item.user_id || 'Anonymous'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Uploaded on: {formatDate(item.uploaded_at)}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={<SensitiveIcon />}
                    label={item.is_sensitive ? "Sensitive Content" : "Not Sensitive"}
                    color={item.is_sensitive ? "error" : "default"}
                    size="small"
                    onClick={() => handleToggleSensitive(item.id, item.is_sensitive, contentType === 'images' ? 'image' : 'video')}
                    sx={{ mr: 1 }}
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => handleApprove(item.id, contentType === 'images' ? 'image' : 'video')}
                  disabled={isSubmitting}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => handleReject(item.id, contentType === 'images' ? 'image' : 'video')}
                  disabled={isSubmitting}
                >
                  Reject
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (isLoading && ((contentType === 'images' && pendingImages.length === 0) || 
                    (contentType === 'videos' && pendingVideos.length === 0))) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Content Moderation
          </Typography>

          <Box>
            {totalContent > 0 && (
              <Button 
                variant="contained" 
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApproveAll}
                disabled={isLoading || isSubmitting}
                sx={{ mr: 2 }}
              >
                Approve All ({totalContent})
              </Button>
            )}

            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={() => fetchPendingContent(page, contentType)}
              disabled={isLoading || isSubmitting}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={contentType} 
            onChange={handleContentTypeChange}
            aria-label="content type tabs"
          >
            <Tab 
              icon={<ImageIcon />} 
              iconPosition="start" 
              label="Images" 
              value="images" 
            />
            <Tab 
              icon={<VideoIcon />} 
              iconPosition="start" 
              label="Videos" 
              value="videos" 
            />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {renderContent()}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange}
              color="primary"
              disabled={isLoading || isSubmitting}
            />
          </Box>
        )}
      </Box>

      {/* Content Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        maxWidth="md"
        fullWidth
      >
        {selectedContent && (
          <>
            <DialogTitle>
              {selectedContent.contentType === 'image' ? 'Image' : 'Video'} Moderation - Wildfire #{selectedContent.wildfire_id}
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <AuthedMedia
                  kind={selectedContent.contentType === 'image' ? 'image' : 'video'}
                  filePath={selectedContent.file_path}
                  alt={`Image #${selectedContent.id}`}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">{selectedContent.contentType === 'image' ? 'Image' : 'Video'} Details</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>ID:</strong> {selectedContent.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Filename:</strong> {selectedContent.file_name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Size:</strong> {Math.round(selectedContent.file_size / 1024)} KB
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {selectedContent.mime_type}
                    </Typography>
                    {selectedContent.contentType === 'video' && selectedContent.duration && (
                      <Typography variant="body2">
                        <strong>Duration:</strong> {Math.floor(selectedContent.duration / 60)}:{(selectedContent.duration % 60).toString().padStart(2, '0')}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Upload Information</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Uploaded by:</strong> {selectedContent.user_id || 'Anonymous'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Uploaded on:</strong> {formatDate(selectedContent.uploaded_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Wildfire ID:</strong> {selectedContent.wildfire_id}
                    </Typography>
                  </Box>
                </Grid>

                {selectedContent.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body2" paragraph>
                      {selectedContent.description}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      icon={<SensitiveIcon />}
                      label={selectedContent.is_sensitive ? "Sensitive Content" : "Not Sensitive"}
                      color={selectedContent.is_sensitive ? "error" : "default"}
                      onClick={() => handleToggleSensitive(
                        selectedContent.id, 
                        selectedContent.is_sensitive, 
                        selectedContent.contentType
                      )}
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Click to toggle sensitivity flag
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => handleReject(selectedContent.id, selectedContent.contentType)} 
                color="error"
                variant="contained"
                startIcon={<RejectIcon />}
                disabled={isSubmitting}
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleApprove(selectedContent.id, selectedContent.contentType)} 
                color="success"
                variant="contained"
                startIcon={<ApproveIcon />}
                disabled={isSubmitting}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ContentModeration;